<template>
    <div class="dk-offer-item tw-w-full tw-flex max-lg:tw-flex-col tw-justify-between">
        <div v-if="versionOrVehicle" class="dk-offer-item__version">
            <div
                class="dk-offer-item__content tw-flex tw-flex-col tw-items-start lg:tw-flex-row lg:tw-items-center tw-gap-2"
            >
                <dk-badge
                    v-if="badge"
                    class="dk-offer-item__badge"
                    :class="{
                        '!tw-bg-primary-light !tw-border-blue-200': !isStockOffer,
                        '!tw-bg-secondary-light !tw-border-border-color': isStockOffer,
                    }"
                    size="small"
                >
                    <span
                        class="dk-offer-item__badge-content tw-whitespace-nowrap"
                        :class="{ 'tw-text-blue-700 tw-mr-2': !isStockOffer, 'tw-text-black': isStockOffer }"
                    >
                        {{ badge }}
                    </span>
                    <dk-icon
                        v-if="!isStockOffer"
                        class="dk-offer-item__badge-icon"
                        name="ds-tag-price-filled"
                        size="xs"
                        color="blue-700"
                    />
                </dk-badge>
                <div class="dk-offer-item__consumption tw-flex tw-items-center tw-gap-4">
                    <dk-e-label
                        v-if="
                            !isStockOffer && versionOrVehicle.efficiencyClass && versionOrVehicle.efficiencyClass.value
                        "
                        class="dk-offer-item__efficiency tw-block"
                        :e-class="versionOrVehicle.efficiencyClass.value.toLowerCase()"
                        size="small"
                        is-vehicle-card
                    >
                        {{ versionOrVehicle.efficiencyClass.value }}
                    </dk-e-label>
                    <h4
                        v-if="versionOrVehicle.name"
                        class="dk-offer-item__consumption-name tw-leading-base lg:tw-text-lg tw-uppercase tw-font-semibold"
                    >
                        {{ versionOrVehicle.name }}
                    </h4>
                </div>
            </div>
            <div
                class="dk-offer-item__version-details tw-flex tw-items-start lg:tw-items-center max-lg:tw-justify-between tw-mt-2 lg:tw-mt-1"
                :class="{ 'lg:tw-mt-0': !isStockOffer, 'lg:tw-mt-1': isStockOffer }"
            >
                <span
                    v-if="item.type && item.type.toLowerCase() === 'offering'"
                    class="dk-offer-item__pricing tw-text-xs tw-leading-xs tw-text-base-subtle tw-font-semibold"
                >
                    {{ item.label }}
                    <del v-if="item.originalPrice" class="tw-ml-1">
                        {{ item.originalPrice }}
                    </del>
                </span>
                <div
                    v-else-if="versionOrVehicle.details && versionOrVehicle.details.length"
                    class="dk-offer-item__details tw-text-base-subtle tw-text-xs tw-font-semibold tw-leading-xs"
                >
                    <span v-for="(detail, i) in versionOrVehicle.details" :key="i" class="dk-offer-item__detail">
                        <span v-if="detail.label" class="dk-offer-item__detail-label">
                            {{ detail.label }}
                        </span>
                        <span class="dk-offer-item__detail-value">
                            {{ detail.value }}
                        </span>
                        <span
                            v-if="i !== versionOrVehicle.details.length - 1"
                            class="dk-offer-item__detail-separator tw-mx-1"
                        >
                            |
                        </span>
                    </span>
                </div>
                <div
                    v-if="versionOrVehicle.disclaimer"
                    class="dk-offer-item__disclaimer tw-flex tw-items-center tw-justify-center tw-ml-1 lg:tw-ml-2"
                    @click="openDisclaimerModal"
                >
                    <dk-icon
                        class="dk-offer-item__disclaimer-icon-mobile tw-mt-px !tw-inline-flex lg:!tw-hidden"
                        name="ds-info-filled"
                        size="s"
                        color="brand-primary"
                    />
                    <dk-icon
                        class="dk-offer-item__disclaimer-icon-desktop !tw-hidden lg:!tw-inline-flex tw-mt-px"
                        name="ds-info-filled"
                        size="xs"
                        color="brand-primary"
                    />
                </div>
            </div>
            <div class="dk-offer-item__specs tw-text-2xs tw-leading-2xs tw-text-placeholder tw-mt-2 lg:tw-mt-1">
                <span
                    v-if="versionOrVehicle.emission && versionOrVehicle.emission.value != undefined"
                    class="dk-offer-item__specs-emission"
                >
                    {{ versionOrVehicle.emission.label }}&nbsp;{{ versionOrVehicle.emission.value }}
                </span>
                <span v-if="versionOrVehicle.consumption" class="dk-offer-item__specs-emission">
                    {{ versionOrVehicle.consumption.label }}&nbsp;{{ versionOrVehicle.consumption.value }}
                </span>
                <template v-if="versionOrVehicle.techSpecs">
                    <span
                        v-for="(techSpec, i) in versionOrVehicle.techSpecs"
                        :key="`vehicle-tech-spec-${i}`"
                        class="dk-offer-item__specs-tech"
                    >
                        <span
                            class="dk-offer-item__specs-tech-item"
                        >{{ techSpec.label }}&nbsp;{{ techSpec.value }}</span>
                        <span
                            v-if="i !== versionOrVehicle.techSpecs.length - 1"
                            class="dk-offer-item__specs-tech-separator"
                        >&nbsp;·&nbsp;</span>
                    </span>
                </template>
            </div>
        </div>
        <div
            class="dk-offer-item__price tw-flex tw-items-start tw-justify-end tw-text-base tw-leading-base tw-mt-4 lg:tw-mt-0"
        >
            <dk-button
                variant="ghost"
                class="tw-flex tw-items-center max-lg:tw-self-end lg:tw-mb-1 tw-text-brand-primary tw-font-bold lg:hover:tw-text-blue-500 lg:tw-duration-300"
                :href="item.href"
                @click="handleOfferClick"
            >
                <span class="dk-offer-item__price-final tw-whitespace-nowrap">
                    {{ item.finalPrice }}
                    <small v-if="item.priceSuffix" class="tw-text-xs tw-leading-xs tw-font-normal">
                        {{ item.priceSuffix }}
                    </small>
                </span>
                <dk-icon class="tw-ml-1" name="ds-chevron-right" size="xs" color="primary" />
            </dk-button>
        </div>
    </div>
</template>

<script>
import gaEventHandler from '@v4/common/components/mixins/google-analytics';
import { dkModalAware } from '@v4/common/components/mixins/modal';
import { redirect } from '@v4/http/request';

export default {
    name: 'DkOfferItem',
    components: {
        DkIcon: () => ({
            component: import(/* webpackChunkName: "DKIcon-OfferItem-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
        }),
        DkBadge: () => ({
            component: import(
                /* webpackChunkName: "DKBadge-OfferItem-vue" */ '@v4/common/components/atoms/DkBadgeDST.vue'
            ),
        }),
        DkELabel: () => ({
            component: import(/* webpackChunkName: "DKELabel-vue" */ '@v4/common/components/atoms/DkELabel.vue'),
        }),
        DkButton: () => ({
            component: import(/* webpackChunkName: "DkButton-vue" */ '@v4/common/components/atoms/DkButton.vue'),
        }),
    },
    mixins: [dkModalAware, gaEventHandler],
    props: {
        /**
         * Offer info object.
         */
        item: {
            type: Object,
            default: () => ({}),
        },
        isStockOffer: {
            type: Boolean,
            default: false,
        },
        oemRedirectUrl: {
            type: String,
            default: null,
        },
    },
    computed: {
        versionOrVehicle() {
            return this.item.version ? this.item.version : this.item.vehicle;
        },
        badge() {
            return this.item.shortLabel
                ? this.item.shortLabel
                : this.item.badgeOfferType
                    ? this.item.badgeOfferType
                    : this.item.label;
        },
    },
    methods: {
        handleOfferClick() {
            this.trackVehiclePricingClick();

            if (this.oemRedirectUrl) {
                // Allow the track requests to finish
                setTimeout(() => {
                    redirect(this.oemRedirectUrl);
                }, 500);
            }
        },
        trackVehiclePricingClick() {
            this.pushGaEvent('Search page', 'vehicle pricing click', `Search page card - ${this.item.type} price`);
            this.$emit('click-select-promotion');
        },
        openDisclaimerModal() {
            return import(
                /* webpackChunkName: "DisclaimerModal-vue" */ '@v4/common/components/atoms/DisclaimerModal'
            ).then(
                async ({ default: DisclaimerModal }) =>
                    await this.showModal(
                        DisclaimerModal,
                        {
                            disclaimer: this.versionOrVehicle.disclaimer,
                        },
                        {
                            name: 'disclaimer-modal',
                            height: 'auto',
                            classes: 'modal--fullscreen_medium',
                            width: 515,
                            scrollable: true,
                        },
                        {},
                        {
                            disableBodyScroll: false,
                        }
                    )
            );
        },
        handlePriceClick(e) {
            e.preventDefault();
            this.$emit('price-clicked');
        },
    },
};
</script>
